/**
 * Typed client for GET /admin-services/exchange-routers.
 *
 * Envelope: the /admin-services/* controller calls utils.handleSuccess()
 * which wraps every response as { success: true, data: <payload> }.
 * client.get() returns { data: <HTTP body> }, so the raw shape coming
 * back from a successful call is:
 *
 *   axios response  →  { data: { success: true, data: <payload> } }
 *
 * unwrap() from '@/api/client' peels off both .data layers to yield
 * the inner payload directly (see client.ts line 40).
 *
 * 404 is the documented "Not configured" signal from the controller —
 * the endpoint is not yet wired in the route file; callers treat it as
 * a null/empty state rather than an error.
 */
import { client, unwrap } from './client';

// ─── Router definitions (static, matches source ROUTERS constant) ─────────

export interface RouterMeta {
  id: 'changenow' | 'exolix' | 'stealthex';
  label: string;
  url: string;
  accent: string;
}

export const ROUTERS: RouterMeta[] = [
  { id: 'changenow', label: 'ChangeNOW', url: 'https://changenow.io',  accent: 'purple' },
  { id: 'exolix',    label: 'Exolix',    url: 'https://exolix.com',    accent: 'teal'   },
  { id: 'stealthex', label: 'StealthEX', url: 'https://stealthex.io',  accent: 'gold'   },
];

// ─── API response types ───────────────────────────────────────────────────

/** Per-router live data returned by the backend */
export interface RouterLive {
  id: string;
  configured: boolean;
  tx24h?: number;
  failed24h?: number;
  avgSpread?: number | null;
  /** Array of daily transaction counts for sparkline (last N days) */
  dailyTx?: number[];
}

/** Per-transaction row in the ledger */
export interface ExchangeTransaction {
  /** Provider-assigned transaction ID */
  id?: string;
  txId?: string;
  router: string;
  status: string;
  fromAsset: string;
  toAsset: string;
  amount: number | string;
  rate?: number | string | null;
  spread?: number | null;
  createdAt: string;
}

/** Aggregate summary KPIs */
export interface ExchangeSummary {
  volume24hUsd?: number;
  tx24h?: number;
  avgSpread?: number | null;
  cheapestRouter?: string;
  /** Map of router → average spread for the comparison panel */
  spreadByRouter?: Record<string, number>;
}

/** Full response payload from /admin-services/exchange-routers */
export interface ExchangeRoutersPayload {
  summary?: ExchangeSummary;
  routers?: RouterLive[];
  transactions?: ExchangeTransaction[];
}

// ─── Fetch function ───────────────────────────────────────────────────────

/**
 * Fetch live exchange-router data.
 *
 * Returns null when the endpoint is not yet wired (404) — the dashboard
 * renders an unconfigured empty state in that case rather than an error.
 * All other non-2xx statuses re-throw so React Query can surface them.
 */
export async function fetchExchangeRouters(): Promise<ExchangeRoutersPayload | null> {
  try {
    return await unwrap<ExchangeRoutersPayload>(
      client.get('/admin-services/exchange-routers')
    );
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      // Endpoint not wired yet / no API keys — render empty state, not error.
      return null;
    }
    throw err;
  }
}
